import type { SafetyWindow } from '@magicschool/business-logic';
import { logger } from '@magicschool/logger';

export type SafetyWindowErrorMode = 'closed' | 'open';
type ErrorOptions = { onError?: SafetyWindowErrorMode };

const MINUTES_PER_DAY = 24 * 60;
export const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;
export const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const ISO_WEEKDAY: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export const parseHHmm = (value: string): number | null => {
  const match = HH_MM.exec(value);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/** Postgres `time` serializes as HH:MM:SS; the app reads HH:mm. */
export const toHHmm = (time: string): string => time.slice(0, 5);

const formatters = new Map<string, Intl.DateTimeFormat>();
const formatterFor = (timezone: string): Intl.DateTimeFormat => {
  let formatter = formatters.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    formatters.set(timezone, formatter);
  }
  return formatter;
};

export const isSupportedTimeZone = (timezone: string): boolean => {
  try {
    formatterFor(timezone);
    return true;
  } catch {
    return false;
  }
};

export type SafetyWindowShape = Pick<SafetyWindow, 'openDays' | 'openStartTime' | 'openEndTime' | 'timezone'>;
export type SafetyWindowIssueCode = 'days-required' | 'time-required' | 'times-differ' | 'end-after-start' | 'timezone-required';
export type SafetyWindowIssue = { field: keyof SafetyWindowShape; code: SafetyWindowIssueCode };

/** The one copy of the paired/differ/valid-days/zone rules; forms, API schemas and the evaluator all call it. */
export const validateSafetyWindowShape = ({ openDays, openStartTime, openEndTime, timezone }: SafetyWindowShape): SafetyWindowIssue[] => {
  const issues: SafetyWindowIssue[] = [];
  if (openDays !== null && (openDays.length === 0 || openDays.some((day) => !Number.isInteger(day) || day < 1 || day > 7))) {
    issues.push({ field: 'openDays', code: 'days-required' });
  }
  if (openStartTime !== null || openEndTime !== null) {
    const start = openStartTime === null ? null : parseHHmm(openStartTime);
    const end = openEndTime === null ? null : parseHHmm(openEndTime);
    if (start === null) issues.push({ field: 'openStartTime', code: 'time-required' });
    if (end === null) issues.push({ field: 'openEndTime', code: 'time-required' });
    if (start !== null && end !== null && start === end) issues.push({ field: 'openEndTime', code: 'times-differ' });
  }
  const daily = openDays !== null || openStartTime !== null || openEndTime !== null;
  if (timezone === null ? daily : !isSupportedTimeZone(timezone)) issues.push({ field: 'timezone', code: 'timezone-required' });
  return issues;
};

/** New hours must close later the same day (00:00 closes at midnight); saved overnight rows still evaluate. */
export const validateSafetyWindowInput = (shape: SafetyWindowShape): SafetyWindowIssue[] => {
  const issues = validateSafetyWindowShape(shape);
  const start = shape.openStartTime === null ? null : parseHHmm(shape.openStartTime);
  const end = shape.openEndTime === null ? null : parseHHmm(shape.openEndTime);
  if (start !== null && end !== null && end !== 0 && end < start) issues.push({ field: 'openEndTime', code: 'end-after-start' });
  return issues;
};

type ZonedClock = { minutesOfDay: number; isoWeekday: number };

const zonedClock = (now: Date, timezone: string): ZonedClock => {
  const parts = formatterFor(timezone).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';
  return { minutesOfDay: Number(value('hour')) * 60 + Number(value('minute')), isoWeekday: ISO_WEEKDAY[value('weekday')] ?? Number.NaN };
};

export const getZonedMinutesOfDay = (now: Date, timezone: string): number => zonedClock(now, timezone).minutesOfDay;

export const getZonedIsoWeekday = (now: Date, timezone: string): number => zonedClock(now, timezone).isoWeekday;

type DailyConstraint = {
  timezone: string;
  openDays: Set<number>;
  hours: { start: number; end: number } | null;
};

type ParsedWindow = {
  startsAt: Date | null;
  endsAt: Date | null;
  daily: DailyConstraint | null;
};

const parseInstant = (value: string | null): Date | null | undefined => {
  if (value === null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const parseWindow = (window: SafetyWindow): ParsedWindow | null => {
  const startsAt = parseInstant(window.startsAt);
  const endsAt = parseInstant(window.endsAt);
  if (startsAt === undefined || endsAt === undefined) return null;
  if (startsAt && endsAt && startsAt >= endsAt) return null;
  if (validateSafetyWindowShape(window).length > 0) return null;

  const start = window.openStartTime === null ? null : parseHHmm(window.openStartTime);
  const end = window.openEndTime === null ? null : parseHHmm(window.openEndTime);
  const hours = start !== null && end !== null ? { start, end } : null;
  if (hours === null && window.openDays === null) return { startsAt, endsAt, daily: null };
  if (window.timezone === null) return null;
  return { startsAt, endsAt, daily: { timezone: window.timezone, openDays: new Set(window.openDays ?? ALL_DAYS), hours } };
};

const isOpenAt = (now: Date, parsed: ParsedWindow): boolean => {
  if (parsed.startsAt && now < parsed.startsAt) return false;
  if (parsed.endsAt && now >= parsed.endsAt) return false;
  if (!parsed.daily) return true;

  const { timezone, openDays, hours } = parsed.daily;
  const { isoWeekday: day, minutesOfDay: minutes } = zonedClock(now, timezone);
  if (!hours) return openDays.has(day);

  if (hours.start < hours.end) return openDays.has(day) && minutes >= hours.start && minutes < hours.end;

  // Overnight hours belong to the day they start on.
  const previousDay = day === 1 ? 7 : day - 1;
  return (openDays.has(day) && minutes >= hours.start) || (openDays.has(previousDay) && minutes < hours.end);
};

export const isSafetyWindowOpen = (now: Date, window: SafetyWindow, { onError = 'closed' }: ErrorOptions = {}): boolean => {
  const parsed = parseWindow(window);
  if (!parsed) {
    logger.error('Invalid student room safety window', { window, onError });
    return onError === 'open';
  }
  return isOpenAt(now, parsed);
};

const LOOKAHEAD_DAYS = 8;
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

const floorToMinute = (date: Date): Date => new Date(Math.floor(date.getTime() / MINUTE_MS) * MINUTE_MS);

// Wall-clock boundaries from yesterday through the lookahead, each probed at the naive offset and an hour either
// side of it, which covers a 60-minute DST shift in both directions and a repeated hour's second pass. A daily
// window shorter than an hour whose boundary falls in a DST gap that is not aligned to a UTC hour (30-minute base
// offsets or shifts) can be found late or missed for that day. Enforcement is unaffected; only the timer is.
const dailyBoundaryCandidates = (from: Date, daily: DailyConstraint): Date[] => {
  const flooredFrom = floorToMinute(from);
  const minutesFrom = getZonedMinutesOfDay(from, daily.timezone);
  const boundaries = daily.hours ? [daily.hours.start, daily.hours.end] : [0];
  const candidates: Date[] = [];
  for (let dayOffset = -1; dayOffset <= LOOKAHEAD_DAYS; dayOffset++) {
    for (const boundary of boundaries) {
      const minutesUntil = ((boundary - minutesFrom + MINUTES_PER_DAY) % MINUTES_PER_DAY) + dayOffset * MINUTES_PER_DAY;
      const naive = new Date(flooredFrom.getTime() + minutesUntil * MINUTE_MS);
      candidates.push(naive, new Date(naive.getTime() - HOUR_MS), new Date(naive.getTime() + HOUR_MS));
    }
  }
  return candidates;
};

// Every hour over the lookahead, so a flip caused by the clock itself (a DST gap or repeat) is bracketed.
const hourlyCandidates = (from: Date): Date[] => {
  const firstHour = Math.ceil(from.getTime() / HOUR_MS) * HOUR_MS;
  return Array.from({ length: LOOKAHEAD_DAYS * 24 }, (_, index) => new Date(firstHour + index * HOUR_MS));
};

// `lower` has the state of `now` and may carry seconds; `upper` is a minute-aligned wall-clock probe that does not.
// Flooring `lower` makes both ends aligned so the midpoint always advances.
const refineToMinute = (lower: Date, upper: Date, parsed: ParsedWindow, openNow: boolean): Date => {
  let low = floorToMinute(lower);
  let high = upper;
  while (high.getTime() - low.getTime() > MINUTE_MS) {
    const mid = floorToMinute(new Date((low.getTime() + high.getTime()) / 2));
    if (isOpenAt(mid, parsed) !== openNow) high = mid;
    else low = mid;
  }
  return high;
};

/**
 * The next instant at which the window flips between open and closed, or null when it does not flip within
 * about a week of `now` (or of `startsAt`, when that is still ahead). Null therefore also covers "never flips".
 */
export const getNextSafetyWindowTransition = (now: Date, window: SafetyWindow): Date | null => {
  const parsed = parseWindow(window);
  if (!parsed) return null;

  const openNow = isOpenAt(now, parsed);
  const candidates: Date[] = [];
  if (parsed.startsAt && parsed.startsAt > now) candidates.push(parsed.startsAt);
  if (parsed.endsAt && parsed.endsAt > now) candidates.push(parsed.endsAt);
  if (parsed.daily) {
    const scanFrom = parsed.startsAt && parsed.startsAt > now ? parsed.startsAt : now;
    candidates.push(...dailyBoundaryCandidates(scanFrom, parsed.daily), ...hourlyCandidates(scanFrom));
  }

  const ordered = candidates.filter((candidate) => candidate > now).sort((a, b) => a.getTime() - b.getTime());
  const flipIndex = ordered.findIndex((candidate) => isOpenAt(candidate, parsed) !== openNow);
  if (flipIndex === -1) return null;

  // An absolute bound is the exact flip instant and may carry seconds; only wall-clock probes need refining.
  const flip = ordered[flipIndex];
  if (flip === parsed.startsAt || flip === parsed.endsAt) return flip;
  return refineToMinute(flipIndex === 0 ? now : ordered[flipIndex - 1], flip, parsed, openNow);
};
